import React from 'react';
import { MoreVertical } from 'lucide-react';
import { IconButton } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useTranslation } from '../i18n';
import MobileSheet from './MobileSheet';

/**
 * A card's secondary actions.
 *
 * A sheet rather than an anchored popover, for the reason §6 gives generally:
 * a 210px menu anchored to a card near the bottom of a phone opens off-screen
 * or under the keyboard, and its rows are sized for a pointer. A sheet is
 * always reachable, always thumb-sized, and gets the back gesture for free.
 *
 * **A blocked action stays visible and says why, in words.** The desktop can
 * put the reason in a `title`; a phone has no hover, so a disabled row with no
 * visible reason is indistinguishable from a broken one. This is the one place
 * where "disabled" is right — an action the viewer LACKS PERMISSION for is not
 * rendered at all, because telling someone about a capability they can never
 * have is worse than silence.
 */
export default function MobileRowMenu({
  /** `[{ id, label, icon, onClick, tone, disabled, disabledReason, separatorBefore }]` */
  items = [],
  /** Names the record the actions apply to, for the trigger's accessible name. */
  subject,
  title,
  'data-testid': testId,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const shown = items.filter(Boolean);
  if (!shown.length) return null;

  return (
    <>
      <IconButton
        icon={<MoreVertical size={18} />}
        label={subject ? t('reception.day.moreActionsFor', { name: subject }) : t('common.actions.more')}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        variant="ghost"
        data-testid={testId ? `${testId}-trigger` : undefined}
      />

      <MobileSheet
        open={open}
        onClose={() => setOpen(false)}
        title={title || subject}
        data-testid={testId}
      >
        <div role="menu" style={{ padding: '0.5rem 0.75rem 1rem', display: 'grid', gap: '0.125rem' }}>
          {shown.map((item) => (
            <React.Fragment key={item.id}>
              {item.separatorBefore ? (
                <hr style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '0.375rem 0' }} />
              ) : null}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                aria-disabled={item.disabled || undefined}
                onClick={() => { setOpen(false); item.onClick?.(); }}
                data-testid={testId ? `${testId}-item-${item.id}` : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.625rem',
                  width: '100%', minHeight: '2.75rem',
                  padding: '0.5rem 0.5rem',
                  background: 'transparent', border: 'none',
                  borderRadius: '0.5rem',
                  textAlign: 'start', font: 'inherit',
                  cursor: item.disabled ? 'default' : 'pointer',
                  color: item.tone === 'danger' ? C.red
                    : item.tone === 'success' ? C.green
                      : C.textPrimary,
                  opacity: item.disabled ? 0.5 : 1,
                }}
              >
                {item.icon ? (
                  <span style={{ display: 'grid', placeItems: 'center', width: 20, flexShrink: 0 }}>
                    {item.icon}
                  </span>
                ) : null}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600 }}>
                    {item.label}
                  </span>
                  {/* The reason a control cannot be used, as text. On a phone
                      there is nowhere else to put it. */}
                  {item.disabled && item.disabledReason ? (
                    <span style={{ display: 'block', fontSize: '0.6875rem', color: C.textMuted, fontWeight: 400 }}>
                      {item.disabledReason}
                    </span>
                  ) : null}
                </span>
              </button>
            </React.Fragment>
          ))}
        </div>
      </MobileSheet>
    </>
  );
}
